import {FinanceLoanClass, FinanceQuote, GenericGrid} from '@amsanalytics/react-shared-library';
import styles from './_finance-payment-stream-display.module.scss';
import {Card, CardHeader, CardTitle} from '@progress/kendo-react-layout';
import { maxPaymentStreams } from '../../../../../utils/quoteUtils';
import {Error} from "@progress/kendo-react-labels";

export interface FinancePaymentStreamDisplayProps {
  selectedFinanceQuote: FinanceQuote;
  selectedFinanceLoanClass: FinanceLoanClass | undefined;
  className?: string;
}

interface IPaymentStreamDisplayItem {
  id: string;
  startDate: Date | null;
  endDate: Date | null;
  term: number;
  payment: number;
  totalAmountToInterest: number;
  totalAmountToPrincipal: number;
  totalDeferredTaxPerPeriod: number;
}

export default function FinancePaymentStreamDisplay(props: FinancePaymentStreamDisplayProps) {
  // Get amortization schedule payments
  const amortizationPayments = [...props.selectedFinanceQuote.financeQuoteAmortizationSchedulePayments]
    .sort((a, b) => {
      if (!a.paymentDate || !b.paymentDate) return 0;
      return new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime();
    });

  // Group consecutive payments by payment amount
  const displayItems: IPaymentStreamDisplayItem[] = [];
  let currentGroup: IPaymentStreamDisplayItem | null = null;

  amortizationPayments.forEach((payment, index) => {
    const paymentAmount = payment.paymentAmount ?? 0;
    const paymentDate = payment.paymentDate ? new Date(payment.paymentDate) : null;

    if (currentGroup === null || currentGroup.payment !== paymentAmount) {
      // Start a new group
      if (currentGroup !== null) {
        displayItems.push(currentGroup);
      }
      currentGroup = {
        id: `group-${index}`,
        startDate: paymentDate,
        endDate: paymentDate,
        term: 1,
        payment: paymentAmount,
        totalAmountToInterest: payment.toInterestAmount ?? 0,
        totalAmountToPrincipal: payment.toPrincipalAmount ?? 0,
        totalDeferredTaxPerPeriod: payment.deferredTaxPerPeriod ?? 0,
      };
    } else {
      // Continue current group - update end date and increment count
      currentGroup.endDate = paymentDate;
      currentGroup.term += 1;
      currentGroup.totalAmountToInterest += payment.toInterestAmount ?? 0;
      currentGroup.totalAmountToPrincipal += payment.toPrincipalAmount ?? 0;
      currentGroup.totalDeferredTaxPerPeriod += payment.deferredTaxPerPeriod ?? 0;
    }
  });

  // Push the last group if it exists
  if (currentGroup !== null) {
    displayItems.push(currentGroup);
  }

  const thereAreMoreThanMaxPaymentStreams = displayItems.length > maxPaymentStreams;
  const cardClassName = thereAreMoreThanMaxPaymentStreams 
    ? `${props.className ?? ''} ${styles.errorBackground}`.trim() 
    : props.className;

  const columns = [
    { title: "Start Date", field: "startDate", width: 100, format: "{0:d}" },
    { title: "End Date", field: "endDate", width: 100, format: "{0:d}" },
    { title: "Count", field: "term", width: 70 },
    { title: "Payment", field: "payment", width: 100, format: "{0:c}" },
    { title: "To Principal", field: "totalAmountToPrincipal", width: 100, format: "{0:c}" },
    { title: "To Interest", field: "totalAmountToInterest", width: 100, format: "{0:c}" },
    ...(props.selectedFinanceLoanClass?.useDeferredSalesTax 
      ? [{ title: "To Deferred Tax", field: "totalDeferredTaxPerPeriod", width: 125, format: "{0:c}" }] 
      : [])
  ];

  return (
    <div className={styles.financePaymentStreamDisplayContainer}>
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Payment Streams (Output)</span>
            </div>
          </CardTitle>
        </CardHeader>
        <div className={styles.sectionItem}>
          {thereAreMoreThanMaxPaymentStreams &&
            <Error>{`The maximum number of payment streams supported is ${maxPaymentStreams}. Please remove payment streams to stay within the limit.`}</Error>
          }
          <GenericGrid<IPaymentStreamDisplayItem>
            size={'small'}
            useIconButtons={true}
            columns={columns}
            items={displayItems}
            disableAdding={true}
            disableEditing={true}
            disableExcelExporting={true}
            disablePdfExporting={true}
            disableFiltering={true}
            disableGrouping={true}
            disablePaging={displayItems.length <= 20}
          />
        </div>
      </Card>
    </div>
  );
}
